import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Stack, useTheme } from "@mui/material";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { useRequestState, useResponsive } from "@app/hooks";
import { filterCars, usedCars } from "../slice";
import { selectFilterCarsState } from "../selector";
import { ProductListContainer } from "./products-lists-containers";
import { ProductListPagination } from "./products.lists-pagination";
import { useForm, useWatch } from "react-hook-form";
import { FormProvider } from "@app/components";
import { Strings } from "@app/constants";
import { selectBrandState } from "@app/modules/categories/selector";

const ProductListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const category = location?.state?.category;
  const isMobile = useResponsive("down", "md");

  const formParamsValues = (location.state as any)?.formParamsValues || {
    searchType: "",
    budget: "",
    kmDriven: "",
    brand: "",
    model: "",
    name: "",
    phone: "",
    email: "",
  };

  const initialPriceRange = [0, 100000000];
  const shortsOptions = [
    {
      label: Strings.productListPage.relevance,
      value: Strings.productListPage.relevanceValues,
    },
    {
      label: Strings.productListPage.priceLowToHigh,
      value: Strings.productListPage.priceLow,
    },
    {
      label: Strings.productListPage.priceHighToLow,
      value: Strings.productListPage.priceHigh,
    },
    {
      label: Strings.productListPage.kmDrivenLow,
      value: Strings.productListPage.kmLow,
    },
    {
      label: Strings.productListPage.yearNewToOld,
      value: Strings.productListPage.yearNew,
    },
  ];

  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState("");
  const [sortValue, setSortValue] = useState("relevance");
  const [priceRange, setPriceRange] = useState<number[]>(initialPriceRange);

  const { data: vechileData, loading }: any = useRequestState({
    stateSelector: selectFilterCarsState,
  });
  const { data: BrandData }: any = useRequestState({
    stateSelector: selectBrandState,
  });

  const methods = useForm({
    defaultValues: {
      brands: [],
      models: [],
      fuel: [],
      year: "",
      kmDriven: "",
      minPrice: 0,
      maxPrice: 100000000,
      search: "",
      transmissions: [],
      color: [],
    },
  });

  const { control, reset } = methods;
  const formValues = useWatch({ control });

  const selectedCount =
    (formValues?.brands?.length || 0) +
    (formValues?.models?.length || 0) +
    (formValues?.fuel?.length || 0) +
    (formValues?.year ? 1 : 0) +
    (formValues?.kmDriven ? 1 : 0);

  useEffect(() => {
    if (!formValues) return;
    dispatch(
      filterCars({
        category,
        page,
        search: formValues?.search,
        sort: sortValue,
        limit: vechileData?.limit,
        min: priceRange?.[0],
        max: priceRange?.[1],
        brandId: formValues?.brands,
        modelId: formValues?.models,
        year: formValues?.year?.slice(0, 4),
        driven: formValues?.kmDriven,
        fuelType: formValues?.fuel,
        transmission: formValues?.transmissions?.[0],
        color: formValues?.color,
      }),
    );
  }, [category, page, sortValue, formValues, priceRange]);

  useEffect(() => {
    if (!formParamsValues || !BrandData?.length) return;

    const brandName =
      typeof formParamsValues.brand === "string" ? formParamsValues.brand : "";
    const modelName =
      typeof formParamsValues.model === "string" ? formParamsValues.model : "";

    const brandMatch = BrandData.find(
      (b: { name: string }) =>
        b.name?.trim().toLowerCase() === brandName?.trim().toLowerCase(),
    );

    const modelMatch = brandMatch?.models?.find(
      (m: { name: string }) =>
        m.name?.trim().toLowerCase() === modelName?.trim().toLowerCase(),
    );

    const [minPrice, maxPrice] = formParamsValues?.budget
      ? formParamsValues.budget.split("-").map((n: string) => Number(n))
      : [0, 100000000];

    reset({
      brands: brandMatch ? [brandMatch.id] : [],
      models: modelMatch ? [modelMatch.id] : [],
      fuel: [],
      year: "",
      kmDriven: formParamsValues.kmDriven || "",
      minPrice,
      maxPrice,
      search: formParamsValues.name || "",
      transmissions: [],
    });
    setPriceRange([minPrice, maxPrice]);
  }, [BrandData, reset]);

  const handleChangePage = (event?: ChangeEvent<unknown>, value?: number) => {
    if (value !== undefined) setPage(value);
  };

  const handleViewDetails = (vehicleId: number) => {
    navigate(`${PATH_DASHBOARD.productDetail}/${vehicleId}`);
  };

  const handleCardSelect = (vehicleId: number) => {
    setSelectedCardId(vehicleId);
  };

  const handleSearchClick = () => {
    const trimmed = inputValue?.trim().toLowerCase();
    setPage(1);
    if (trimmed === "") {
      dispatch(
        usedCars({
          category: category || "New Cars",
          limit: "10",
          page: "1",
        }),
      );
    }
  };

  const handleClear = () => {
    reset({});
  };

  return (
    <Stack flex={1}>
      <FormProvider methods={methods}>
        <ProductListContainer
          filteredData={vechileData?.data}
          selectedCardId={selectedCardId}
          onSelect={handleCardSelect}
          onViewDetails={handleViewDetails}
          isMobile={isMobile}
          shortsOptions={shortsOptions}
          sortValue={sortValue}
          setSortValue={setSortValue}
          selectedCount={selectedCount}
          handleClear={handleClear}
          priceRange={priceRange}
          setPriceRange={setPriceRange}
          loading={loading}
        />
        {vechileData?.data?.length > 0 && vechileData && (
          <ProductListPagination
            data={vechileData}
            page={page}
            handleChangePage={handleChangePage}
            theme={theme}
          />
        )}
      </FormProvider>
    </Stack>
  );
};

export { ProductListPage };
